/**
 * Mock - quick search: jobs, results, history.
 * Per-domain mutable state lives in ./db.ts (never in the shared mock/db.ts).
 *
 * The prototype ran a search as a `setTimeout(SEARCH_MS)` inside the view and
 * then called the pure `quickSearchResults`. Here the timer is the job: `start`
 * answers at once with a job id, the job completes `SEARCH_MS` later (computing
 * the hits and writing the history row, as the prototype's timer callback did),
 * and `getQuickSearchJob` reports `running` until then — so the client polls
 * exactly as it will against the backend.
 *
 * No endpoint here waits on `simulate`'s latency: the prototype drew every one
 * of these instantly from its own state (the search's five seconds is the job's
 * clock, not request latency). Each still honours `?mock-error=<method>`.
 */
import type {
  QuickSearchApi,
  QuickSearchHistoryEntry,
  QuickSearchRequest,
  QuickSearchResult,
} from '@/data-sources/contract';
import { HttpError } from '@/lib/errors';

import { postUrl } from '../engine/posts';
import {
  DEFAULT_MAX_RESULTS,
  MAX_TERMS,
  quickSearchChannels,
  quickSearchResults,
  type QuickSearchQuery,
  type QuickSearchResult as EngineResult,
} from '../engine/quickSearch';
import { latencyMs, simulate } from '../scenarios';
import { quickSearchDb, ranAtOf, type MockJob } from './db';

/**
 * How long a search runs — the prototype's `SEARCH_MS`, the user's figure
 * (2026-09-21). Long enough that `Searching` reads as a search running.
 */
export const SEARCH_MS = 5000;

/** `?latency=0` makes the search instant too (QA); any other value keeps the prototype's five seconds. */
export function searchMs(): number {
  return latencyMs() === 0 ? 0 : SEARCH_MS;
}

/** The engine query a request (or a history entry) runs as. `country` filters nothing — see the engine. */
function engineQuery(
  channel: string,
  terms: readonly string[],
  maxResults: number,
  tiktok: QuickSearchRequest['tiktok'],
): QuickSearchQuery {
  return {
    channel,
    terms,
    maxResults,
    ...(tiktok ? { mode: tiktok.mode, sort: tiktok.sort, days: tiktok.days } : {}),
  };
}

function toResult(hit: EngineResult): QuickSearchResult {
  return {
    id: String(hit.id),
    text: hit.conversation,
    url: postUrl(hit.row),
    reactions: hit.reactions,
    comments: hit.comments,
    shares: hit.shares,
    postedAt: new Date(hit.postedAt).toISOString(),
    dashboardStatus: quickSearchDb.added.has(String(hit.id)) ? 'processing' : null,
  };
}

function validate(request: QuickSearchRequest): void {
  if (request.terms.length === 0) throw new HttpError(422, 'Enter at least one search term.');
  if (request.terms.length > MAX_TERMS)
    throw new HttpError(422, `Enter at most ${MAX_TERMS} search terms.`);
  if (!Number.isInteger(request.maxResults) || request.maxResults < 1) {
    throw new HttpError(422, 'Max results must be a whole number of at least 1.');
  }
}

/** The prototype's timer callback: compute the hits, prepend the history row. */
function complete(job: MockJob, sequence: number): void {
  const { request } = job;
  const hits = quickSearchResults(
    engineQuery(request.channel, request.terms, request.maxResults, request.tiktok),
  );
  const entry: QuickSearchHistoryEntry = {
    id: `run-${sequence}`,
    terms: [...request.terms],
    channel: request.channel,
    ...(request.tiktok ? { tiktok: { ...request.tiktok } } : {}),
    /* `All time` unless TikTok's window was set — the prototype's `SESSION_RANGE` rule. */
    rangeDays: request.tiktok?.days ?? null,
    ranAt: ranAtOf(sequence),
  };
  quickSearchDb.history = [entry, ...quickSearchDb.history];
  job.status = 'done';
  job.hits = hits;
  job.historyEntryId = entry.id;
}

function findEntry(id: string): QuickSearchHistoryEntry {
  const entry = quickSearchDb.history.find((e) => e.id === id);
  if (!entry) throw new HttpError(404, `No quick search history entry "${id}".`);
  return entry;
}

export const mockQuickSearchApi: QuickSearchApi = {
  async listQuickSearchChannels(ctx) {
    await simulate('listQuickSearchChannels', ctx.signal, { latency: false });
    /* The prototype's picker offered every channel of the `Channels` chip. */
    return [...quickSearchChannels];
  },

  async startQuickSearch(request, ctx) {
    await simulate('startQuickSearch', ctx.signal, { latency: false });
    validate(request);
    const sequence = ++quickSearchDb.sequence;
    const job: MockJob = {
      jobId: `job-${sequence}`,
      request: {
        ...request,
        terms: [...request.terms],
        ...(request.tiktok ? { tiktok: { ...request.tiktok } } : {}),
      },
      status: 'running',
      hits: null,
      historyEntryId: null,
    };
    quickSearchDb.jobs.set(job.jobId, job);
    const ms = searchMs();
    if (ms <= 0) {
      complete(job, sequence);
    } else {
      const timer = setTimeout(() => {
        quickSearchDb.timers.delete(timer);
        complete(job, sequence);
      }, ms);
      quickSearchDb.timers.add(timer);
    }
    return { jobId: job.jobId };
  },

  async getQuickSearchJob({ jobId }, ctx) {
    await simulate('getQuickSearchJob', ctx.signal, { latency: false });
    const job = quickSearchDb.jobs.get(jobId);
    if (!job) throw new HttpError(404, `No quick search job "${jobId}".`);
    return {
      jobId: job.jobId,
      status: job.status,
      request: job.request,
      historyEntryId: job.historyEntryId,
      results: job.hits ? job.hits.map(toResult) : null,
    };
  },

  async listQuickSearchHistory(ctx) {
    await simulate('listQuickSearchHistory', ctx.signal, { latency: false });
    return quickSearchDb.history.map((entry) => ({ ...entry, terms: [...entry.terms] }));
  },

  async getQuickSearchHistoryEntry({ id }, ctx) {
    await simulate('getQuickSearchHistoryEntry', ctx.signal, { latency: false });
    const entry = findEntry(id);
    /*
     * Re-run, not recorded — the prototype's call: the corpus is fixed and the
     * search pure, so the entry's own terms return the rows it returned. At
     * `DEFAULT_MAX_RESULTS`, since the entry does not record its cap.
     */
    const hits = quickSearchResults(
      engineQuery(entry.channel, entry.terms, DEFAULT_MAX_RESULTS, entry.tiktok),
    );
    return { entry: { ...entry, terms: [...entry.terms] }, results: hits.map(toResult) };
  },

  async deleteQuickSearchHistoryEntry({ id }, ctx) {
    await simulate('deleteQuickSearchHistoryEntry', ctx.signal, { latency: false });
    quickSearchDb.history = quickSearchDb.history.filter((entry) => entry.id !== id);
  },

  async addQuickSearchResultsToDashboard({ postIds }, ctx) {
    await simulate('addQuickSearchResultsToDashboard', ctx.signal, { latency: false });
    for (const id of postIds) quickSearchDb.added.add(id);
    return { postIds: [...postIds], status: 'processing' };
  },

  async exportQuickSearchResults(request, ctx) {
    await simulate('exportQuickSearchResults', ctx.signal, { latency: false });
    if (request.jobId === undefined && request.historyEntryId === undefined) {
      throw new HttpError(422, 'Name the search to export.');
    }
    /* The prototype's `Export` dialog produces nothing on Confirm; the mock queues nothing either. */
    return { exportId: `export-${++quickSearchDb.sequence}`, status: 'queued' };
  },
};
